import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

const VARIANTS = {
  primary:
    'bg-primary-500 text-ink-950 shadow-[0_4px_0_var(--color-primary-700)] hover:bg-primary-400 active:translate-y-[3px] active:shadow-[0_1px_0_var(--color-primary-700)]',
  secondary:
    'bg-ink-700 text-paper shadow-[0_4px_0_var(--color-ink-850)] hover:bg-ink-600 active:translate-y-[3px] active:shadow-[0_1px_0_var(--color-ink-850)]',
  success:
    'bg-success-400 text-ink-950 shadow-[0_4px_0_var(--color-success-500)] hover:bg-success-300 active:translate-y-[3px] active:shadow-[0_1px_0_var(--color-success-500)]',
  ghost: 'bg-transparent text-ink-200 hover:bg-white/8 hover:text-paper',
} as const;

const SIZES = {
  sm: 'h-9 px-3.5 text-sm rounded-xl gap-1.5',
  md: 'h-11 px-5 text-base rounded-2xl gap-2',
  lg: 'h-14 px-7 text-lg rounded-2xl gap-2.5',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'relative inline-flex items-center justify-center font-display font-bold tracking-tight whitespace-nowrap',
        'transition-[background-color,transform,box-shadow,opacity] duration-100 select-none',
        'disabled:opacity-45 disabled:active:translate-y-0',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading && <Spinner className="absolute size-5" />}
      <span className={cn('inline-flex items-center gap-[inherit]', loading && 'invisible')}>{children}</span>
    </button>
  );
});
